// A seed's tracks rendered whole, on a page that has no room open.
//
// The same road the browser editor takes: the snapshot hydrated into a room,
// the room into an arrangement, every clip scheduled where it sits — then
// each lane rendered offline on a player nobody hears. Null when the seed has
// no arrangement to hydrate (a stem-shaped upload: its stems already ARE the
// full-length tracks).

import type { SeedApiDetail } from "@shared/api-types.js";
import type { Snapshot } from "@shared/seed-history.js";
import { hasHydratableContent, planHydration } from "@shared/sync/hydrate.js";
import { ArrangementPlayer, beatsToSeconds } from "@web-shared/lib/audio-engine";
import { fromRoom, laneInstruments } from "@web-shared/lib/arrangement";
import { patchById } from "@web-shared/lib/synth";
import { effectiveTempo, midiSources, roomSourcesWithUrls } from "@web-shared/lib/player-sources";
import { cleanTrackName } from "@/lib/track-name";
import type { StemLane } from "@/lib/stems";

export interface SeedStemRenderer {
  lanes: StemLane[];
  totalSec: number;
  render: (laneId: string) => Promise<AudioBuffer>;
  dispose: () => void;
}

export function seedStemRenderer(
  seed: Pick<SeedApiDetail, "arrangement" | "bpm">,
  urlOf: (audioKey: string) => string | undefined,
): SeedStemRenderer | null {
  const snapshot = seed.arrangement as Snapshot | null | undefined;
  if (!snapshot || !hasHydratableContent(snapshot)) return null;
  let n = 0;
  const room = planHydration(snapshot, (kind) => `dl_${kind[0]}_${++n}`, () => true)?.room;
  if (!room) return null;
  const { tempo } = effectiveTempo(room, seed.bpm);
  const arrangement = fromRoom(room.tracks, room.clips, room.sceneNames ?? undefined);
  const sources = roomSourcesWithUrls(arrangement, tempo, urlOf);
  const midi = midiSources(arrangement, tempo);
  const sounding = new Set([...sources.map((s) => s.laneId), ...midi.map((s) => s.laneId)]);
  const lanes = arrangement.lanes.filter((l) => sounding.has(l.id)).map((l) => ({ id: l.id, name: cleanTrackName(l.name) }));
  if (lanes.length === 0) return null;

  const player = new ArrangementPlayer();
  player.tempo = tempo;
  player.setMidiSources(midi);
  for (const lane of arrangement.lanes) player.setLaneLevel(lane.id, { volume: lane.volume, pan: lane.pan });
  for (const [laneId, id] of Object.entries(laneInstruments(arrangement))) player.setLanePatch(laneId, patchById(id));
  return {
    lanes,
    totalSec: beatsToSeconds(arrangement.length, tempo),
    render: (laneId) => player.renderMixdown(sources, undefined, (id) => id === laneId, false),
    dispose: () => player.dispose(),
  };
}
